import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Zap, Check, X, Undo2, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

type BoostRow = {
  id: string;
  idea_id: string;
  user_id: string;
  lane: string | null;
  moderation_status: string;
  scheduled_start_at: string | null;
  scheduled_end_at: string | null;
  created_at: string;
  provider_payment_id: string | null;
  ideas?: { title: string | null } | null;
  boost_packages?: { name: string | null; duration_days: number | null } | null;
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  rejected: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  refunded: "bg-muted text-muted-foreground border-border",
};

export default function BoostsAdminTab() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"pending" | "all">("pending");
  const [noteById, setNoteById] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data: boosts, isLoading } = useQuery({
    queryKey: ["admin-boosts", filter],
    queryFn: async () => {
      let q = (supabase as any)
        .from("idea_boosts")
        .select("*, ideas(title), boost_packages(name, duration_days)")
        .order("created_at", { ascending: false })
        .limit(100);
      if (filter === "pending") q = q.eq("moderation_status", "pending");
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as BoostRow[];
    },
  });

  const act = async (boost: BoostRow, action: "approve" | "reject" | "refund") => {
    setBusyId(boost.id);
    try {
      const { data, error } = await supabase.functions.invoke("refund-boost", {
        body: { boostId: boost.id, action, note: noteById[boost.id] || null },
      });
      if (error) throw new Error(error.message);
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(
        action === "approve" ? "Boost approved & live" :
        action === "reject" ? "Boost rejected" : "Refund issued"
      );
      qc.invalidateQueries({ queryKey: ["admin-boosts"] });
    } catch (e: any) {
      toast.error("Action failed", { description: e.message });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-400" /> Boost moderation
          </h2>
          <p className="text-sm text-muted-foreground">
            Approve, reject, or refund paid boosts before they surface in featured lanes.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant={filter === "pending" ? "default" : "outline"} onClick={() => setFilter("pending")}>
            Pending
          </Button>
          <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")}>
            All recent
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading boosts…</div>
      ) : !boosts?.length ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
          No {filter === "pending" ? "pending" : ""} boosts.
        </div>
      ) : (
        <div className="space-y-3">
          {boosts.map((b) => {
            const start = b.scheduled_start_at ? new Date(b.scheduled_start_at) : null;
            const end = b.scheduled_end_at ? new Date(b.scheduled_end_at) : null;
            return (
              <div key={b.id} className="rounded-xl border border-border bg-card p-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge className={`border ${STATUS_COLORS[b.moderation_status] ?? STATUS_COLORS.pending}`}>
                        {b.moderation_status}
                      </Badge>
                      {b.lane && (
                        <span className="text-[10px] uppercase tracking-wide bg-muted px-1.5 py-0.5 rounded">
                          {b.lane}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {b.boost_packages?.name} · {b.boost_packages?.duration_days}d
                      </span>
                      <span className="text-xs text-muted-foreground">
                        purchased {formatDistanceToNow(new Date(b.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <a
                      href={`/ideas/${b.idea_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-display font-semibold text-foreground hover:text-primary truncate block"
                    >
                      {b.ideas?.title || `Idea ${b.idea_id.slice(0, 8)}`}
                    </a>
                    {(start || end) && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
                        <Clock className="h-3 w-3" />
                        {start?.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                        {end && <> → {end.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</>}
                      </div>
                    )}
                  </div>
                </div>

                {b.moderation_status === "pending" && (
                  <>
                    <Textarea
                      placeholder="Internal note (optional, attached to refund/rejection)"
                      rows={2}
                      value={noteById[b.id] || ""}
                      onChange={(e) => setNoteById((p) => ({ ...p, [b.id]: e.target.value }))}
                    />
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" onClick={() => act(b, "approve")} disabled={busyId === b.id}>
                        <Check className="h-4 w-4 mr-1.5" /> Approve & go live
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => act(b, "reject")} disabled={busyId === b.id}>
                        <X className="h-4 w-4 mr-1.5" /> Reject (no refund)
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => act(b, "refund")} disabled={busyId === b.id}>
                        <Undo2 className="h-4 w-4 mr-1.5" /> Refund
                      </Button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}